import { buildControllerRules } from "@gettactic/helpers/src/hook-form/buildControllerRules";
import type React from "react";
import { Controller, type FieldValues } from "react-hook-form";
import { PhoneInput } from "./PhoneInput";
import type { HookFormPhoneInputProps } from "./PhoneInput.types";

const MIN_PHONE_DIGITS = 7;

/**
 * Checks that a phone string contains enough digits to be a real number,
 * not just a bare country dial code like "+1".
 */
const isValidPhoneNumber = (value: unknown): boolean => {
  if (typeof value !== "string") return false;
  const digits = value.replace(/\D/g, "");
  return digits.length >= MIN_PHONE_DIGITS;
};

export const HookFormPhoneInput = <T extends FieldValues>({
  form,
  name,
  rules,
  ...rest
}: HookFormPhoneInputProps<T>): React.ReactElement => {
  const phoneRules = {
    ...rules,
    validate: {
      ...(typeof rules?.validate === "object" ? rules.validate : {}),
      phoneFormat: (value: unknown) => {
        if (!value || value === "+") return true;
        // Treat bare country dial codes (e.g. "+1", "+39", "+353") as empty
        const isBareDialCode =
          typeof value === "string" && /^\+\d{1,4}$/.test(value.trim());
        if (isBareDialCode) {
          return rest.isRequired
            ? "This field is required"
            : true;
        }
        return (
          isValidPhoneNumber(value) || "Please enter a valid phone number"
        );
      }
    }
  };

  return (
    <Controller
      control={form.control}
      name={name}
      rules={buildControllerRules({
        form,
        name,
        rules: phoneRules,
        isRequired: rest.isRequired
      })}
      render={({ field, fieldState }) => (
        <PhoneInput
          value={field.value}
          onChange={field.onChange}
          error={fieldState.error?.message}
          {...rest}
        />
      )}
    />
  );
};
